import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  HStack,
  VStack,
  Icon,
  Badge,
  Skeleton,
  Tooltip,
  IconButton,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spacer,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiChevronUp,
  FiChevronDown,
  FiMoreVertical,
  FiEye,
  FiEdit,
  FiTrash2,
} from 'react-icons/fi';

const TABLE_VARIANTS = {
  default: {
    container: {
      bg: 'white',
      borderRadius: 'xl',
      shadow: 'lg',
      border: '1px',
      borderColor: 'gray.200',
      overflow: 'hidden',
    },
    header: {
      bg: 'gray.50',
      borderBottom: '2px',
      borderColor: 'gray.200',
    },
    row: {
      _hover: {
        bg: 'gray.50',
        transform: 'translateX(2px)',
        transition: 'all 0.2s ease',
      },
    },
  },
  glass: {
    container: {
      bg: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      borderRadius: 'xl',
      border: '1px',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
    },
    header: {
      bg: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    row: {
      _hover: {
        bg: 'rgba(255, 255, 255, 0.1)',
        transform: 'translateX(2px)',
        transition: 'all 0.3s ease',
      },
    },
  },
  gradient: {
    container: {
      bg: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
      borderRadius: 'xl',
      shadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      border: '1px',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      overflow: 'hidden',
    },
    header: {
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderBottom: 'none',
    },
    row: {
      _hover: {
        bg: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
        transform: 'translateX(3px)',
        transition: 'all 0.3s ease',
      },
    },
  },
  minimal: {
    container: {
      bg: 'transparent',
      borderRadius: 'none',
      shadow: 'none',
      border: 'none',
      overflow: 'visible',
    },
    header: {
      bg: 'transparent',
      borderBottom: '2px',
      borderColor: 'gray.200',
    },
    row: {
      borderBottom: '1px',
      borderColor: 'gray.100',
      _hover: {
        bg: 'gray.50',
        transition: 'all 0.2s ease',
      },
    },
  },
};

const SIZE_VARIANTS = {
  sm: {
    table: { size: 'sm' },
    text: { fontSize: 'sm' },
    padding: { px: 3, py: 2 },
  },
  md: {
    table: { size: 'md' },
    text: { fontSize: 'md' },
    padding: { px: 4, py: 3 },
  },
  lg: {
    table: { size: 'lg' },
    text: { fontSize: 'lg' },
    padding: { px: 6, py: 4 },
  },
};

const ModernTable = ({
  data = [],
  columns = [],
  variant = 'default',
  size = 'md',
  loading = false,
  searchable = true,
  filterable = true,
  sortable = true,
  selectable = false,
  actions = [],
  onRowClick,
  onSelectionChange,
  getRowProps,
  emptyMessage = 'Veri bulunamadı',
  searchPlaceholder = 'Ara...',
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tableVariant = TABLE_VARIANTS[variant] || TABLE_VARIANTS.default;
  const sizeVariant = SIZE_VARIANTS[size] || SIZE_VARIANTS.md;

  const bgColor = 'white';
  const borderColor = 'gray.200';
  const textColor = 'gray.700';

  // Filtreleme ve arama
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row =>
      columns.some(column => {
        const value = row[column.dataIndex];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Sıralama
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Sayfalama
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(paginatedData.map((_, index) => index)));
    } else {
      setSelectedRows(new Set());
    }
    onSelectionChange?.(checked ? paginatedData : []);
  };

  const handleSelectRow = (index, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedRows(newSelected);

    const selectedData = paginatedData.filter((_, i) => newSelected.has(i));
    onSelectionChange?.(selectedData);
  };

  const renderCell = (column, row, rowIndex) => {
    if (column.render) {
      return column.render(row[column.dataIndex], row, rowIndex);
    }
    return row[column.dataIndex];
  };

  const renderActions = (row, rowIndex) => {
    if (!actions || actions.length === 0) return null;

    return (
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={FiMoreVertical} />}
          variant="ghost"
          size="sm"
          onClick={(e) => e.stopPropagation()}
        />
        <MenuList>
          {actions.map((action, index) => (
            <MenuItem
              key={index}
              icon={action.icon && <Icon as={action.icon} />}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.(row, rowIndex);
              }}
              color={action.color}
            >
              {action.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  };

  return (
    <Box {...tableVariant.container} {...props}>
      {/* Toolbar */}
      {(searchable || filterable) && (
        <Flex p={4} gap={4} align="center" borderBottom="1px" borderColor={borderColor}>
          {searchable && (
            <InputGroup maxW="300px">
              <InputLeftElement>
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
                }}
              />
            </InputGroup>
          )}

          <Spacer />

          <HStack spacing={2}>
            {filterable && (
              <IconButton
                icon={<Icon as={FiFilter} />}
                variant="ghost"
                size="sm"
                aria-label="Filter"
              />
            )}
            <IconButton
              icon={<Icon as={FiRefreshCw} />}
              variant="ghost"
              size="sm"
              aria-label="Refresh"
            />
            <IconButton
              icon={<Icon as={FiDownload} />}
              variant="ghost"
              size="sm"
              aria-label="Export"
            />
          </HStack>
        </Flex>
      )}

      {/* Table */}
      <TableContainer>
        <Table {...sizeVariant.table}>
          <Thead {...tableVariant.header}>
            <Tr>
              {selectable && (
                <Th {...sizeVariant.padding}>
                  <Checkbox
                    isChecked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    isIndeterminate={selectedRows.size > 0 && selectedRows.size < paginatedData.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </Th>
              )}
              {columns.map((column) => (
                <Th
                  key={column.dataIndex}
                  {...sizeVariant.padding}
                  cursor={sortable && column.sortable !== false ? 'pointer' : 'default'}
                  onClick={() => column.sortable !== false && handleSort(column.dataIndex)}
                  position="relative"
                >
                  <Flex align="center" gap={2}>
                    <Text {...sizeVariant.text} fontWeight="bold">
                      {column.title}
                    </Text>
                    {sortable && column.sortable !== false && (
                      <VStack spacing={0}>
                        <Icon
                          as={FiChevronUp}
                          boxSize={3}
                          color={
                            sortConfig.key === column.dataIndex && sortConfig.direction === 'asc'
                              ? 'blue.500'
                              : 'gray.400'
                          }
                        />
                        <Icon
                          as={FiChevronDown}
                          boxSize={3}
                          color={
                            sortConfig.key === column.dataIndex && sortConfig.direction === 'desc'
                              ? 'blue.500'
                              : 'gray.400'
                          }
                        />
                      </VStack>
                    )}
                  </Flex>
                </Th>
              ))}
              {actions && actions.length > 0 && (
                <Th {...sizeVariant.padding} width="60px">
                  <Text {...sizeVariant.text} fontWeight="bold">
                    İşlemler
                  </Text>
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: pageSize }).map((_, index) => (
                <Tr key={index}>
                  {selectable && (
                    <Td {...sizeVariant.padding}>
                      <Skeleton height="20px" width="20px" />
                    </Td>
                  )}
                  {columns.map((column) => (
                    <Td key={column.dataIndex} {...sizeVariant.padding}>
                      <Skeleton height="20px" />
                    </Td>
                  ))}
                  {actions && actions.length > 0 && (
                    <Td {...sizeVariant.padding}>
                      <Skeleton height="20px" width="30px" />
                    </Td>
                  )}
                </Tr>
              ))
            ) : paginatedData.length === 0 ? (
              // Empty state
              <Tr>
                <Td
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions?.length > 0 ? 1 : 0)}
                  textAlign="center"
                  py={8}
                >
                  <Text color="gray.500" {...sizeVariant.text}>
                    {emptyMessage}
                  </Text>
                </Td>
              </Tr>
            ) : (
              // Data rows
              paginatedData.map((row, index) => {
                const extraRowProps = (typeof getRowProps === "function" ? getRowProps(row, index) : null) || {};
                const baseBg = selectedRows.has(index) ? 'blue.50' : 'transparent';
                const mergedBg = selectedRows.has(index) ? (extraRowProps.bg || baseBg) : (extraRowProps.bg || baseBg);
                return (
                  <Tr
                    key={index}
                    {...tableVariant.row}
                    {...extraRowProps}
                    cursor={onRowClick ? 'pointer' : 'default'}
                    onClick={() => onRowClick?.(row, index)}
                    bg={mergedBg}
                  >
                    {selectable && (
                      <Td {...sizeVariant.padding}>
                        <Checkbox
                          isChecked={selectedRows.has(index)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectRow(index, e.target.checked);
                          }}
                        />
                      </Td>
                    )}
                    {columns.map((column) => (
                      <Td key={column.dataIndex} {...sizeVariant.padding}>
                        <Text {...sizeVariant.text}>
                          {renderCell(column, row, index)}
                        </Text>
                      </Td>
                    ))}
                    {actions && actions.length > 0 && (
                      <Td {...sizeVariant.padding}>
                        {renderActions(row, index)}
                      </Td>
                    )}
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex p={4} justify="space-between" align="center" borderTop="1px" borderColor={borderColor}>
          <HStack spacing={2}>
            <Text fontSize="sm" color={textColor}>
              Sayfa başına:
            </Text>
            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              width="80px"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Select>
          </HStack>

          <Text fontSize="sm" color={textColor}>
            {sortedData.length} kayıttan {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, sortedData.length)} arası gösteriliyor
          </Text>

          <HStack spacing={1}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              isDisabled={currentPage === 1}
            >
              Önceki
            </Button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
              if (pageNum > totalPages) return null;

              return (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={currentPage === pageNum ? 'solid' : 'ghost'}
                  colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              isDisabled={currentPage === totalPages}
            >
              Sonraki
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

// Preset table components
export const GlassTable = (props) => <ModernTable variant="glass" {...props} />;
export const GradientTable = (props) => <ModernTable variant="gradient" {...props} />;
export const MinimalTable = (props) => <ModernTable variant="minimal" {...props} />;

// Common action presets
export const TABLE_ACTIONS = {
  view: {
    label: 'Görüntüle',
    icon: FiEye,
    color: 'blue.500',
  },
  edit: {
    label: 'Düzenle',
    icon: FiEdit,
    color: 'green.500',
  },
  delete: {
    label: 'Sil',
    icon: FiTrash2,
    color: 'red.500',
  },
};

export default ModernTable;
